import type { Dispatch, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import type { QueryClient } from '@tanstack/react-query';
import type { NavigateFunction } from 'react-router-dom';
import type { DailyQuestStartTarget, DailyQuestView } from '../../../models/quest';
import type { NavigationConfirmDestination } from '../types';
import { cn } from '../../cn';
import DailyQuestCardInfo from './DailyQuestCardInfo';
import DailyQuestCardXActions from './DailyQuestCardXActions';
import DailyQuestCardRestActions from './DailyQuestCardRestActions';
import DailyQuestCardClaimButton from './DailyQuestCardClaimButton';


export interface DailyQuestCardProps {
  quest: DailyQuestView;
  t: TFunction;
  queryClient: QueryClient;
  navigate: NavigateFunction;
  onClose: () => void;
  isXFollowQuest: (questId: number) => boolean;
  getXUrlForQuest: (questId: number) => string;
  calculateTimeUntilAvailable: (questId: number) => string;
  setShowVipInfoModal: Dispatch<SetStateAction<boolean>>;
  isXQuestOnModeration: (questId: number) => boolean;
  getXQuestModerationTimestamp: (questId: number) => number | null;
  getXQuestVerificationStartTimestamp: (questId: number) => number | null;
  clearXQuestModerationTimestamp: (questId: number) => void;
  clearXQuestVerificationStartTimestamp: (questId: number) => void;
  setActiveXModalQuestId: Dispatch<SetStateAction<number>>;
  setShowModerationInfoModal: Dispatch<SetStateAction<boolean>>;
  handleSubscribeToX: (questId: number) => void;
  handleVerifyXSubscription: (questId: number, userQuestId?: number) => Promise<void>;
  startedVerificationQuestIds: Set<number>;
  isVerifyingQuest: number | null;
  setIsVerifyingQuest: Dispatch<SetStateAction<number | null>>;
  setStartedVerificationQuestIds: Dispatch<SetStateAction<Set<number>>>;
  setCloudStorageItem: (key: string, value: string, callback?: (success: boolean) => void) => void;
  handleStartAction: (quest: Pick<DailyQuestView, 'questId' | 'userQuestId' | 'objectives'>) => Promise<void>;
  showingAdQuestIds: Set<number>;
  setNavigationConfirmModal: Dispatch<SetStateAction<{
    isOpen: boolean;
    destination: NavigationConfirmDestination;
  }>>;
  needsStartButton: (quest: DailyQuestStartTarget) => boolean;
  handleClaimReward: (userQuestId: number) => Promise<void>;
  claimingQuestIds: Set<number>;
}

export default function DailyQuestCard({
  quest,
  t,
  queryClient,
  navigate,
  onClose,
  isXFollowQuest,
  getXUrlForQuest,
  calculateTimeUntilAvailable,
  setShowVipInfoModal,
  isXQuestOnModeration,
  getXQuestModerationTimestamp,
  getXQuestVerificationStartTimestamp,
  clearXQuestModerationTimestamp,
  clearXQuestVerificationStartTimestamp,
  setActiveXModalQuestId,
  setShowModerationInfoModal,
  handleSubscribeToX,
  handleVerifyXSubscription,
  startedVerificationQuestIds,
  isVerifyingQuest,
  setIsVerifyingQuest,
  setStartedVerificationQuestIds,
  setCloudStorageItem,
  handleStartAction,
  showingAdQuestIds,
  setNavigationConfirmModal,
  needsStartButton,
  handleClaimReward,
  claimingQuestIds,
}: DailyQuestCardProps) {
  // Робимо квест сірим одразу після успішного claim (оптимістично теж),
  // щоб UI не "підсвічувався" до наступного refetch.
  const shouldBeGrayed = quest.isFinished && quest.isRewardClaimed && !quest.isRepeatable;

  return (
    <article
      key={quest.questId}
      className={cn(
        'flex flex-col gap-3xl rounded-sm px-2xl py-lg',
        shouldBeGrayed
          ? 'bg-[image:var(--gradient-disabled-progress)] opacity-60'
          : 'bg-[image:var(--gradient-brand-deep)]',
      )}
    >
      <div
        className={cn(
          'flex flex-col',
          quest.progressText ? 'gap-xl' : 'gap-lg',
          shouldBeGrayed ? 'text-text-soft' : 'text-white',
        )}
      >
        <DailyQuestCardInfo
          quest={quest}
          t={t}
          isXFollowQuest={isXFollowQuest}
          getXUrlForQuest={getXUrlForQuest}
          calculateTimeUntilAvailable={calculateTimeUntilAvailable}
          setShowVipInfoModal={setShowVipInfoModal}
          isXQuestOnModeration={isXQuestOnModeration}
        />
      </div>
      {isXFollowQuest(quest.questId) ? (
        <DailyQuestCardXActions
          quest={quest}
          t={t}
          isXFollowQuest={isXFollowQuest}
          isXQuestOnModeration={isXQuestOnModeration}
          getXQuestModerationTimestamp={getXQuestModerationTimestamp}
          getXQuestVerificationStartTimestamp={getXQuestVerificationStartTimestamp}
          clearXQuestModerationTimestamp={clearXQuestModerationTimestamp}
          clearXQuestVerificationStartTimestamp={clearXQuestVerificationStartTimestamp}
          setActiveXModalQuestId={setActiveXModalQuestId}
          setShowModerationInfoModal={setShowModerationInfoModal}
          handleSubscribeToX={handleSubscribeToX}
          handleVerifyXSubscription={handleVerifyXSubscription}
        />
      ) : (
        <DailyQuestCardRestActions
          quest={quest}
          t={t}
          queryClient={queryClient}
          navigate={navigate}
          onClose={onClose}
          startedVerificationQuestIds={startedVerificationQuestIds}
          isVerifyingQuest={isVerifyingQuest}
          setIsVerifyingQuest={setIsVerifyingQuest}
          setStartedVerificationQuestIds={setStartedVerificationQuestIds}
          setCloudStorageItem={setCloudStorageItem}
          handleStartAction={handleStartAction}
          showingAdQuestIds={showingAdQuestIds}
          setNavigationConfirmModal={setNavigationConfirmModal}
          needsStartButton={needsStartButton}
        />
      )}
      <DailyQuestCardClaimButton
        quest={quest}
        t={t}
        isXFollowQuest={isXFollowQuest}
        isXQuestOnModeration={isXQuestOnModeration}
        needsStartButton={needsStartButton}
        handleClaimReward={handleClaimReward}
        claimingQuestIds={claimingQuestIds}
      />
    </article>
  );
}
